import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import Hls from 'hls.js';
import { getApiUrl } from '../lib/apiUrl';
import { isPlaybackIdentifier } from '../lib/recordingPlayback';
import { useAuthStore } from '../store/authStore';

export function RecordingPlaybackPage() {
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const recordingId = query.get('recordingId');
  const uploadSessionId = query.get('uploadSessionId');
  const token = useAuthStore((s) => s.token);
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setError(null);
    setReady(false);
    if (!token) return;
    if (!isPlaybackIdentifier(recordingId) || !isPlaybackIdentifier(uploadSessionId)) {
      setError('This video link is invalid. Ask ChatGPT for a new playback link.');
      return;
    }
    const element = video.current;
    if (!element) return;
    if (!Hls.isSupported()) {
      setError(
        'This browser cannot play protected HLS video. Open this link in a current Chrome, Edge, Firefox or Safari browser with Media Source support.',
      );
      return;
    }
    const base = new URL(
      getApiUrl(
        `/media-processing/recordings/${encodeURIComponent(recordingId)}/clips/${encodeURIComponent(uploadSessionId)}/replay`,
      ),
      window.location.origin,
    ).href;
    const hls = new Hls({
      xhrSetup: (xhr, requestedUrl) => {
        const url = new URL(requestedUrl, base);
        // Never forward a bearer token to an external manifest reference or another API path.
        if (!url.href.startsWith(`${base}/`)) throw new Error('Unexpected replay asset');
        xhr.open('GET', url.href, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      },
    });
    const fail = () =>
      setError(
        'This video is unavailable or your sign-in has expired. Sign in to an authorized Paddiq account, then reopen this link.',
      );
    element.addEventListener('error', fail);
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (data.fatal) {
        setReady(false);
        fail();
        hls.stopLoad();
      }
    });
    hls.on(Hls.Events.MANIFEST_PARSED, () => setReady(true));
    hls.loadSource(`${base}/manifest`);
    hls.attachMedia(element);
    return () => {
      hls.destroy();
      element.removeEventListener('error', fail);
      element.removeAttribute('src');
      element.load();
    };
  }, [token, recordingId, uploadSessionId]);
  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 2, sm: 4 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2}>
          <Typography variant="h4">Paddiq recording</Typography>
          {!token && (
            <Alert severity="info">
              Sign in to Paddiq in another tab, then reopen this video link.
            </Alert>
          )}
          {!token && (
            <Button href="/login" target="_blank" rel="noopener noreferrer">
              Sign in to Paddiq
            </Button>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {token && !ready && !error && <Typography role="status">Loading your video…</Typography>}
          {token && (
            <Box
              component="video"
              ref={video}
              controls
              playsInline
              aria-label="Stored Paddiq recording"
              sx={{ width: '100%', bgcolor: '#000', borderRadius: 1 }}
            />
          )}
          <Typography variant="body2" color="text.secondary">
            Only people with access to this recording can watch it.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
